import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

/**
 * A capturable section snapshot, registered by a content component so the
 * header's "Download Snapshots" action can package them into a ZIP.
 *
 * The registry deliberately holds no DOM refs: capture is data-driven
 * (`captureChartAsBlob` / `captureSummaryAsBlob` render a fresh offscreen tree),
 * so a section contributes its current view purely through `getBlob`.
 */
export interface InsightsSnapshot {
  /** Stable id, unique within a page (e.g. 'summary', 'metrics'). */
  id: string
  /** Filename inside the ZIP, e.g. 'summary.png'. */
  filename: string
  /** Archive order; entries are captured/added lowest-first. */
  order: number
  /** Captures the section's current view to a PNG blob. */
  getBlob: () => Promise<Blob>
}

interface SnapshotRegistryApi {
  register: (snapshot: InsightsSnapshot) => void
  unregister: (id: string) => void
}

// Two contexts by design. The write API is stable for the provider's lifetime,
// so a section registering never re-renders the other (heavy) sections nor
// re-fires their register effect. Only the list changes on registration, and it
// is consumed solely by the header — bundling both in one value would make every
// registration invalidate the value and loop the register effect.
const SnapshotRegistryContext = createContext<SnapshotRegistryApi | null>(null)
const SnapshotListContext = createContext<InsightsSnapshot[] | null>(null)

/**
 * Page-scoped registry shared by the Insights header (which triggers the bulk
 * download) and the content sections (which register their capturable views).
 * One instance per page — see the single-channel and all-channels wrappers.
 */
export function InsightsSnapshotProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const [snapshotsById, setSnapshotsById] = useState<
    ReadonlyMap<string, InsightsSnapshot>
  >(() => new Map())

  const register = useCallback((snapshot: InsightsSnapshot) => {
    setSnapshotsById((prev) => {
      const next = new Map(prev)
      next.set(snapshot.id, snapshot)
      return next
    })
  }, [])

  const unregister = useCallback((id: string) => {
    setSnapshotsById((prev) => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  // Stable identity: register/unregister never change, so producers depending on
  // this API don't re-run their effects (or re-render) when the list changes.
  const registryApi = useMemo(
    () => ({ register, unregister }),
    [register, unregister],
  )

  const snapshots = useMemo(
    () => [...snapshotsById.values()].toSorted((a, b) => a.order - b.order),
    [snapshotsById],
  )

  return (
    <SnapshotRegistryContext.Provider value={registryApi}>
      <SnapshotListContext.Provider value={snapshots}>
        {children}
      </SnapshotListContext.Provider>
    </SnapshotRegistryContext.Provider>
  )
}

InsightsSnapshotProvider.displayName = 'InsightsSnapshotProvider'

/** A snapshot a section wants registered, plus whether it currently has data. */
export interface RegisterableSnapshot extends InsightsSnapshot {
  /** Registered only while true; flipping to false unregisters it. */
  available: boolean
}

/**
 * Registers every currently-available snapshot in `entries` and unregisters
 * them when they drop out or the section unmounts. Sections with multiple view
 * variants (e.g. Followers Bar/Line/Growth) register them all in one call.
 *
 * The effect only re-syncs when the registered *set* changes (its ids,
 * filenames, orders, and availability) — captured via `signature` — not when a
 * `getBlob` closure's identity changes each render. Each registered `getBlob`
 * reads the latest matching entry from a ref, so a date/data/view change
 * refreshes the captured image without churning the registry.
 *
 * A missing provider is tolerated (a no-op) so sections can render standalone in
 * tests and Storybook. The header's {@link useInsightsSnapshots} still enforces
 * the provider where it matters.
 */
export function useRegisterSnapshots(entries: RegisterableSnapshot[]): void {
  const registry = useContext(SnapshotRegistryContext)

  const entriesRef = useRef(entries)
  entriesRef.current = entries

  const signature = entries
    .filter((entry) => entry.available)
    .map((entry) => `${entry.id}:${entry.filename}:${entry.order}`)
    .join('|')

  useEffect(() => {
    if (!registry) return
    const active = entriesRef.current.filter((entry) => entry.available)
    for (const entry of active) {
      registry.register({
        id: entry.id,
        filename: entry.filename,
        order: entry.order,
        getBlob: (): Promise<Blob> =>
          (
            entriesRef.current.find((current) => current.id === entry.id) ??
            entry
          ).getBlob(),
      })
    }
    return (): void => {
      for (const entry of active) registry.unregister(entry.id)
    }
  }, [registry, signature])
}

/**
 * Registers a single section snapshot. Thin wrapper over
 * {@link useRegisterSnapshots} for sections with just one view (e.g. Summary).
 */
export function useRegisterSnapshot(entry: RegisterableSnapshot): void {
  useRegisterSnapshots([entry])
}

/**
 * The currently-capturable snapshots for the page, sorted by archive order.
 * `canDownload` gates the header's "Download Snapshots" menu item.
 */
export function useInsightsSnapshots(): {
  snapshots: InsightsSnapshot[]
  canDownload: boolean
} {
  const snapshots = useContext(SnapshotListContext)
  if (snapshots === null) {
    throw new Error(
      'useInsightsSnapshots must be used within an InsightsSnapshotProvider.',
    )
  }
  return { snapshots, canDownload: snapshots.length > 0 }
}
